import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL } from "../../config/api";
import { logout } from "./authSlice";
import activityService from "../../services/activityService";
import { applyOptimisticReaction, canonicalReactionId, getActiveWorkflowByCategory, isWorkflowCreatingWorkItemType } from "../../utils/reactionConfig";
import {
  readMessagesFromRealm,
  readOlderMessagesFromRealm,
  readMessagesAroundTargetFromRealm,
  readLastViewedFromRealm,
  computeUnreadCountsFromRealm,
  upsertRealmDocs,
  backfillChannelMessages,
  realmAvailable,
  queryRealm,
} from "../../realm";
import {
  writePendingMessageToRealm,
  markMessageSynced,
  flushOutboundMessageQueue,
  queuePendingReaction,
  rollbackQueuedReaction,
  reconcileJourneyTasksForMessage,
  isNetworkFailure,
} from "../../realm/outboundQueue";
import {
  attachmentsForQueue,
  isPendingLocalAttachment,
  resolveAttachmentsForSend,
  toServerAttachments,
} from "../../services/offlineAttachmentStore";
import { mergeStickyDeliveryFields, preferAttachmentsWithUrls } from "../../utils/messageDeliveryStatus";

const pendingReactionKeys = new Set();

const messageDocId = (msg) => String(msg?.id || msg?._id || msg?.tempId || '');

const isHiddenFromUser = (message, userId) => {
  if (!userId || !message) return false;
  const hidden = message.hiddenFromUserIds;
  if (!Array.isArray(hidden) || hidden.length === 0) return false;
  const me = String(userId);
  return hidden.some((id) => String(id) === me);
};

const asMessageArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.messages)) return payload.messages;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const isClientOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;

const messageIdsMatch = (message, messageId) => {
  const id = String(messageId || '');
  if (!id) return false;
  return String(message?.id || message?._id || '') === id
    || String(message?.tempId || '') === id
    || String(message?.clientMessageId || '') === id;
};

const findMessageLocation = (state, messageId, preferredChannelId) => {
  if (!messageId) return null;
  if (preferredChannelId && Array.isArray(state.messages[preferredChannelId])) {
    const index = state.messages[preferredChannelId].findIndex((message) =>
      messageIdsMatch(message, messageId)
    );
    if (index !== -1) return { channelId: preferredChannelId, index };
  }
  const channels = Object.keys(state.messages || {});
  for (let i = 0; i < channels.length; i += 1) {
    const channelId = channels[i];
    const list = state.messages[channelId];
    if (!Array.isArray(list)) continue;
    const index = list.findIndex((message) => messageIdsMatch(message, messageId));
    if (index !== -1) return { channelId, index };
  }
  return null;
};

const bumpLastActivity = (state, channelId, message) => {
  if (!channelId) return;
  const ts = new Date(message?.createdAt || message?.timestamp || 0).getTime();
  if (!Number.isFinite(ts) || ts <= 0) return;
  if (!state.lastActivityByChannelId) state.lastActivityByChannelId = {};
  const prev = state.lastActivityByChannelId[channelId] || 0;
  if (ts > prev) state.lastActivityByChannelId[channelId] = ts;
};

const getReactionKey = (emoji) => {
  if (!emoji || typeof emoji !== "string") return null;
  return canonicalReactionId(emoji) || null;
};

const reactionFailurePayload = (error, restore) => {
  const raw = error?.response?.data;
  let message = "Failed to add reaction";
  if (typeof raw === "string" && raw.trim()) {
    message = raw.replace(/^Error:\s*/i, "").trim();
  } else if (raw && typeof raw === "object") {
    const fromBody = raw.error || raw.message;
    if (fromBody) message = String(fromBody).replace(/^Error:\s*/i, "").trim();
  }
  return { error: message, restore };
};

// Async thunks
export const fetchMessages = createAsyncThunk(
  "chat/fetchMessages",
  async ({ channelId, page = 0, size = 30, skipRealm = false }, { rejectWithValue }) => {
    const readCached = () => readMessagesFromRealm(channelId, { limit: Math.max(size, 80) });

    if (!skipRealm && page === 0 && !isClientOnline()) {
      const cached = await readCached();
      if (cached?.length) {
        return { channelId, messages: cached.slice(0, size), fromRealm: true };
      }
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/messages/channel/${channelId}`,
        {
          params: { page, size },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const list = asMessageArray(response.data);
      Promise.resolve(upsertRealmDocs("Message", list)).catch(() => {});
      return { channelId, messages: list };
    } catch (error) {
      if (page === 0) {
        const cached = await readCached();
        if (cached?.length) {
          return { channelId, messages: cached, fromRealm: true };
        }
      }
      return rejectWithValue(
        error.response?.data || "Failed to fetch messages"
      );
    }
  }
);

export const loadOlderMessages = createAsyncThunk(
  "chat/loadOlderMessages",
  async (
    { channelId, page = 1, size = 30, beforeDate = null },
    { getState, rejectWithValue }
  ) => {
    const existing = getState()?.chat?.messages?.[channelId] || [];
    const before = beforeDate || existing[0]?.createdAt || null;
    const excludeIds = existing.map((msg) => msg.id || msg._id).filter(Boolean);
    const requestTimeout = size >= 200 ? 30000 : 8000;

    const readLocal = async () =>
      readOlderMessagesFromRealm(channelId, {
        beforeDate: before,
        size,
        excludeIds,
      });

    const fetchFromApi = async () => {
      const token = localStorage.getItem("token");
      const params = before
        ? { beforeDate: before, size }
        : { page, size };
      const response = await axios.get(
        `${API_URL}/messages/channel/${channelId}`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          timeout: requestTimeout,
        }
      );
      const list = Array.isArray(response.data) ? response.data : [];
      upsertRealmDocs("Message", list);
      return list;
    };

    if (await realmAvailable()) {
      const local = await readLocal();
      const canBackfill = typeof navigator === "undefined" || navigator.onLine !== false;
      if (local.length) {
        if (local.length < size && canBackfill) {
          backfillChannelMessages(channelId, before || new Date().toISOString(), {
            limit: Math.max(size, 80),
            timeout: requestTimeout,
          }).catch(() => {});
        }
        return {
          channelId,
          page,
          beforeDate: before,
          messages: local,
          fromRealm: true,
        };
      }
      if (canBackfill) {
        await backfillChannelMessages(channelId, before || new Date().toISOString(), {
          limit: Math.max(size, 80),
          timeout: requestTimeout,
        });
        const filled = await readLocal();
        if (filled.length) {
          return {
            channelId,
            page,
            beforeDate: before,
            messages: filled,
            fromRealm: true,
          };
        }
      }
    }

    try {
      const list = await fetchFromApi();
      return { channelId, page, beforeDate: before, messages: list };
    } catch (error) {
      const local = await readLocal();
      if (local.length || isNetworkFailure(error)) {
        return {
          channelId,
          page,
          beforeDate: before,
          messages: local,
          fromRealm: true,
        };
      }
      return rejectWithValue(
        error.response?.data || "Failed to load older messages"
      );
    }
  }
);

export const fetchMessagesAroundTarget = createAsyncThunk(
  "chat/fetchMessagesAroundTarget",
  async (
    { channelId, messageId, before = 40, after = 15 },
    { getState, rejectWithValue }
  ) => {
    const epochAtStart = getState()?.chat?.aroundFetchEpoch || 0;
    const local = await readMessagesAroundTargetFromRealm(channelId, messageId, {
      before,
      after,
    });
    const localHasTarget = Boolean(
      local?.messages?.some((m) => String(m.id || m._id) === String(messageId))
    );
    if (localHasTarget) {
      return {
        channelId: local.channelId || channelId,
        messageId,
        messages: local.messages,
        hasMoreBefore: local.hasMoreBefore,
        hasMoreAfter: local.hasMoreAfter,
        fromRealm: true,
        epochAtStart,
      };
    }

    // Realm miss must fall through to API — do not reject just because Realm is open.
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/messages/channel/${channelId}/around/${messageId}`,
        {
          params: { before, after },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        }
      );
      const messages = response.data.messages || [];
      upsertRealmDocs("Message", messages);
      return {
        channelId,
        messageId,
        messages,
        hasMoreBefore: response.data.hasMoreBefore ?? false,
        hasMoreAfter: response.data.hasMoreAfter ?? false,
        epochAtStart,
      };
    } catch (error) {
      if (local?.messages?.length) {
        // Offline / API down: return whatever Realm had even if target id was missing.
        return {
          channelId: local.channelId || channelId,
          messageId,
          messages: local.messages,
          hasMoreBefore: local.hasMoreBefore,
          hasMoreAfter: local.hasMoreAfter,
          fromRealm: true,
          epochAtStart,
        };
      }
      return rejectWithValue(
        error.response?.data || "Failed to load messages around target"
      );
    }
  }
);

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (
    {
      content,
      channelId,
      replyToMessageId,
      mentionedUserIds,
      attachments,
      linkPreview,
      attachmentGroupName,
      targetResourceGroupId,
      eventInfo,
      workItemShare,
      tempId,
      senderId,
      sensitive,
      senderPin,
      allowReceiverPin,
      sensitiveTitle,
    },
    { rejectWithValue }
  ) => {
    let outgoingAttachments = attachments || [];
    try {
      if (
        outgoingAttachments.some(isPendingLocalAttachment)
        && !(typeof navigator !== "undefined" && navigator.onLine === false)
      ) {
        outgoingAttachments = await resolveAttachmentsForSend(outgoingAttachments);
      }
    } catch (error) {
      if (error?.code === 'ERR_FILE_MISSING' || (error?.response?.status && error.response.status < 500)) {
        return rejectWithValue({
          data: error.response?.data || error.message || "Failed to upload attachments",
          status: error.response?.status,
          originalError: error,
        });
      }
      outgoingAttachments = attachments || [];
    }

    const persistableAttachments = attachmentsForQueue(outgoingAttachments);
    const stillPendingLocal = persistableAttachments.some(isPendingLocalAttachment);
    const serverAttachments = stillPendingLocal ? [] : toServerAttachments(persistableAttachments);

    const requestBody = {
      content: content || "",
      channelId,
      replyToMessageId,
      mentionedUserIds,
    };
    if (sensitive) {
      requestBody.sensitive = true;
      requestBody.allowReceiverPin = allowReceiverPin === true;
      if (sensitiveTitle) {
        requestBody.sensitiveTitle = sensitiveTitle;
      }
      if (senderPin) {
        requestBody.senderPin = senderPin;
      }
    }
    if (tempId) {
      requestBody.clientMessageId = tempId;
    }
    if (serverAttachments.length > 0) {
      requestBody.attachments = serverAttachments;
    }
    if (attachmentGroupName) {
      requestBody.attachmentGroupName = attachmentGroupName;
    }
    if (targetResourceGroupId) {
      requestBody.targetResourceGroupId = targetResourceGroupId;
    }
    if (linkPreview) {
      requestBody.linkPreview = linkPreview;
    }
    if (eventInfo?.eventId) {
      requestBody.eventInfo = eventInfo;
    }
    if (workItemShare?.itemId) {
      requestBody.workItemShare = workItemShare;
    }

    let localMessage = null;
    try {
      localMessage = await writePendingMessageToRealm({
        channelId,
        content,
        senderId,
        replyToMessageId,
        mentionedUserIds,
        attachments: persistableAttachments,
        linkPreview,
        attachmentGroupName,
        targetResourceGroupId,
        tempId,
        workItemShare,
        sensitive,
        senderPin,
        allowReceiverPin,
        sensitiveTitle,
      });
    } catch (err) {
      console.warn("[realm] local write before send failed", err);
    }

    const queuedPayload = localMessage
      ? {
          ...localMessage,
          queued: true,
          syncStatus: "pending",
          tempId: tempId || localMessage.tempId,
          attachments: outgoingAttachments.length ? outgoingAttachments : persistableAttachments,
        }
      : null;

    if (stillPendingLocal) {
      if (queuedPayload) {
        return queuedPayload;
      }
      return rejectWithValue({
        data: "Can't upload files while offline. Please try again when you're back online.",
        status: 0,
      });
    }

    if (queuedPayload && typeof navigator !== "undefined" && navigator.onLine === false) {
      return queuedPayload;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/messages`, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      if (localMessage?.id) {
        await markMessageSynced(localMessage.id, response.data);
      } else {
        await upsertRealmDocs("Message", [{ ...response.data, syncStatus: "synced" }]);
      }
      return response.data;
    } catch (error) {
      if (queuedPayload && isNetworkFailure(error)) {
        return queuedPayload;
      }
      return rejectWithValue({
        data: error.response?.data || "Failed to send message",
        status: error.response?.status,
        originalError: error,
      });
    }
  }
);

export const flushOutboundMessages = createAsyncThunk(
  "chat/flushOutboundMessages",
  async () => {
    const result = await flushOutboundMessageQueue();
    return result.synced || [];
  }
);

export const addReaction = createAsyncThunk(
  "chat/addReaction",
  async ({
    messageId,
    emoji,
    completionTimeInMinutes,
    urgentDeadlineAt,
    workflowTitle,
    workflowDescription,
    workflowSpaceId,
    workflowSpaceName,
    channelId,
    userId,
  }, { getState, rejectWithValue }) => {
    const reactionKey = getReactionKey(emoji);
    const pendingKey = `${messageId}:${reactionKey}`;
    const state = getState();
    const channelMessages = (channelId && state.chat?.messages?.[channelId]) || [];
    const existing = channelMessages.find((item) => item.id === messageId || item.tempId === messageId)
      || { id: messageId, channelId };
    const snapshot = {
      ...existing,
      reactions: existing.reactions,
      workflowByCategory: existing.workflowByCategory,
      completionTimeInMinutes: existing.completionTimeInMinutes,
      urgentDeadlineAt: existing.urgentDeadlineAt,
      workflowTitle: existing.workflowTitle,
      workflowDescription: existing.workflowDescription,
      workflowSpaceId: existing.workflowSpaceId,
      workflowSpaceName: existing.workflowSpaceName,
    };
    const title = typeof workflowTitle === 'string' ? workflowTitle.trim() : '';
    const descriptionProvided = workflowDescription !== undefined && workflowDescription !== null;
    let queued = null;
    const queueWrite = queuePendingReaction({
      message: existing,
      emoji: reactionKey,
      completionTimeInMinutes,
      urgentDeadlineAt,
      workflowTitle: title || undefined,
      workflowDescription: descriptionProvided ? workflowDescription : undefined,
      workflowSpaceId,
      workflowSpaceName,
      userId,
    }).then((row) => {
      queued = row;
      return row;
    }).catch((err) => {
      console.warn('[chat] local reaction write failed', err);
      return null;
    });

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const offlineRow = await queueWrite;
      if (offlineRow) return offlineRow;
    }

    try {
      const token = localStorage.getItem("token");
      const body = { emoji: reactionKey };
      if (completionTimeInMinutes != null && Number(completionTimeInMinutes) > 0) {
        body.completionTimeInMinutes = Number(completionTimeInMinutes);
      }
      if (urgentDeadlineAt) {
        body.urgentDeadlineAt = String(urgentDeadlineAt);
      }
      if (title) {
        body.workflowTitle = title.slice(0, 200);
      }
      if (descriptionProvided) {
        body.workflowDescription = String(workflowDescription);
      }
      if (typeof workflowSpaceId === 'string' && workflowSpaceId.trim()) {
        body.workflowSpaceId = workflowSpaceId.trim();
      }
      if (typeof workflowSpaceName === 'string' && workflowSpaceName.trim()) {
        body.workflowSpaceName = workflowSpaceName.trim();
      }
      const response = await axios.post(
        `${API_URL}/messages/${messageId}/reaction`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      if (queued?.id) {
        markMessageSynced(queued.id, response.data).catch(() => {});
      } else {
        upsertRealmDocs('Message', [{
          ...response.data,
          syncStatus: 'synced',
          outboundOp: null,
          outboundRequest: null,
        }]).catch(() => {});
      }
      if (isWorkflowCreatingWorkItemType(reactionKey)) {
        reconcileJourneyTasksForMessage(messageId).catch(() => {});
      }
      return response.data;
    } catch (error) {
      if (queued && isNetworkFailure(error)) {
        return queued;
      }
      try {
        await rollbackQueuedReaction(snapshot);
      } catch {
        // keep going so the UI can still show the error
      }
      return rejectWithValue(reactionFailurePayload(error, {
        messageId,
        channelId,
        reactions: snapshot.reactions,
        workflowByCategory: snapshot.workflowByCategory,
        completionTimeInMinutes: snapshot.completionTimeInMinutes,
        workflowTitle: snapshot.workflowTitle,
        workflowDescription: snapshot.workflowDescription,
        workflowSpaceId: snapshot.workflowSpaceId,
        workflowSpaceName: snapshot.workflowSpaceName,
      }));
    } finally {
      pendingReactionKeys.delete(pendingKey);
    }
  },
  {
    condition: ({ messageId, emoji }) => {
      const reactionKey = getReactionKey(emoji);
      if (!messageId || !reactionKey) return false;
      const pendingKey = `${messageId}:${reactionKey}`;
      if (pendingReactionKeys.has(pendingKey)) {
        return false;
      }
      pendingReactionKeys.add(pendingKey);
      return true;
    },
  }
);

export const updateWorkflowTrack = createAsyncThunk(
  "chat/updateWorkflowTrack",
  async ({ messageId, trackId, trackLink, clear }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const shouldClear = Boolean(clear)
        || ((!trackId || !String(trackId).trim()) && (!trackLink || !String(trackLink).trim()));
      const response = shouldClear
        ? await axios.delete(
            `${API_URL}/messages/${messageId}/workflow-track`,
            { headers },
          )
        : await axios.patch(
            `${API_URL}/messages/${messageId}/workflow-track`,
            { trackId, trackLink },
            { headers },
          );
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      let message = "Failed to update track ID";
      if (typeof data === "string" && data.trim()) {
        message = data.replace(/^Error:\s*/i, "");
      } else if (data && typeof data === "object") {
        message = data.message || data.error || message;
      }
      return rejectWithValue(message);
    }
  }
);

export const addMessageCustomTag = createAsyncThunk(
  "chat/addMessageCustomTag",
  async ({ messageId, label, link }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/messages/${messageId}/custom-tags`,
        { label, link: link || undefined },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add tag");
    }
  }
);

export const removeMessageCustomTag = createAsyncThunk(
  "chat/removeMessageCustomTag",
  async ({ messageId, tagId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${API_URL}/messages/${messageId}/custom-tags/${tagId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to remove tag");
    }
  }
);

export const assignWorkflowMessage = createAsyncThunk(
  "chat/assignWorkflowMessage",
  async ({ messageId, userId, userName, channelId }, { getState, rejectWithValue }) => {
    const state = getState();
    const location = findMessageLocation(state.chat, messageId, channelId);
    const existing = location
      ? state.chat.messages[location.channelId][location.index]
      : null;
    const restore = existing
      ? {
          channelId: location.channelId,
          messageId: existing.id || messageId,
          workflowAssignedToUserId: existing.workflowAssignedToUserId,
          workflowAssignedToName: existing.workflowAssignedToName,
        }
      : null;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("workloop:workflows-assigned-changed", {
        detail: {
          action: "assignee",
          messageId,
          assignedToUserId: userId || null,
          assignedToName: userName || "",
          channelId: channelId || existing?.channelId || null,
        },
      }));
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${API_URL}/messages/${messageId}/workflow-assign`,
        { userId },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      const raw = error?.response?.data;
      return rejectWithValue({
        error: (typeof raw === "string" ? raw : raw?.error) || "Failed to assign workflow item",
        restore,
      });
    }
  }
);

export const fetchUnreadCounts = createAsyncThunk(
  "chat/fetchUnreadCounts",
  async ({ channelIds, lastViewed }, { getState, rejectWithValue }) => {
    const currentUserId = getState()?.auth?.user?.id;
    const latestLastViewed = mergeLastViewedMaps(
      lastViewed,
      getState()?.chat?.lastViewed || {}
    );
    const local = await computeUnreadCountsFromRealm(
      channelIds,
      latestLastViewed,
      currentUserId
    );
    if (local) {
      return local;
    }
    if (await realmAvailable()) {
      return Object.fromEntries((channelIds || []).map((id) => [id, 0]));
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/messages/unread-counts`,
        { channelIds, lastViewed: latestLastViewed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch unread counts"
      );
    }
  },
  {
    // Drop duplicate in-flight / back-to-back unread polls (mount + interval overlap).
    condition: (_, { getState }) => {
      const status = getState()?.chat?.unreadCountsStatus;
      return status !== "pending";
    },
  }
);

// Fetch messages by date range (and optional user) for a channel
export const fetchMessagesByDateRange = createAsyncThunk(
  "chat/fetchMessagesByDateRange",
  async (
    { channelId, startDate, endDate, userId = null },
    { rejectWithValue }
  ) => {
    const startIso = typeof startDate === "string" ? startDate : new Date(startDate).toISOString();
    const endIso = typeof endDate === "string" ? endDate : new Date(endDate).toISOString();
    const startTs = new Date(startIso).getTime();
    const endTs = new Date(endIso).getTime();

    if (await realmAvailable()) {
      const local = await readMessagesFromRealm(channelId, { limit: 5000 });
      const messages = (local || []).filter((msg) => {
        const ts = new Date(msg.createdAt || msg.timestamp || 0).getTime();
        if (!Number.isFinite(ts) || ts < startTs || ts > endTs) return false;
        if (userId && String(msg.senderId) !== String(userId)) return false;
        return true;
      });
      return {
        channelId,
        startDate: startIso,
        endDate: endIso,
        userId: userId || null,
        messages,
        fromRealm: true,
      };
    }

    try {
      const token = localStorage.getItem("token");
      const params = {
        channelId,
        startDate: startIso,
        endDate: endIso,
      };
      if (userId && userId.trim() !== "") {
        params.userId = userId.trim();
      }
      const response = await axios.get(
        `${API_URL}/messages/by-date-range`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return {
        channelId,
        startDate: startIso,
        endDate: endIso,
        userId: params.userId || null,
        messages: response.data,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch messages by date range"
      );
    }
  }
);

// Fetch lastViewed timestamps from backend
export const fetchLastViewed = createAsyncThunk(
  "chat/fetchLastViewed",
  async (_, { getState, rejectWithValue }) => {
    const current = getState()?.chat?.lastViewed || {};
    const local = await readLastViewedFromRealm();
    if (local) {
      return mergeLastViewedMaps(current, local);
    }
    if (await realmAvailable()) {
      return current;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/messages/last-viewed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return mergeLastViewedMaps(current, response.data || {});
    } catch (error) {
      console.error("Failed to fetch lastViewed from backend:", error);
      return rejectWithValue(
        error.response?.data || "Failed to fetch last viewed"
      );
    }
  }
);

// Save lastViewed timestamp to backend
export const saveLastViewedToBackend = createAsyncThunk(
  "chat/saveLastViewedToBackend",
  async (channelId, { getState, rejectWithValue }) => {
    const timestamp = new Date().toISOString();
    const currentUserId = getState()?.auth?.user?.id;
    try {
      const existing = await queryRealm({
        type: "UserChannelView",
        filter: "channelId == $0",
        args: [String(channelId)],
      });
      const docId = existing?.[0]?.id || existing?.[0]?._id || `${currentUserId || "local"}:${channelId}`;
      upsertRealmDocs("UserChannelView", [{
        ...(existing?.[0] || {}),
        id: docId,
        _id: docId,
        userId: currentUserId || existing?.[0]?.userId,
        channelId,
        lastViewed: timestamp,
        updatedAt: timestamp,
      }]);
    } catch {
      // local persist is best-effort
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/messages/channel/${channelId}/viewed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      if (!isNetworkFailure(error) && error?.response) {
        console.error("Failed to save lastViewed to backend:", error);
      }
    }
    return { channelId, timestamp };
  }
);

// Load lastViewed from localStorage on initialization
const loadLastViewedFromStorage = () => {
  try {
    const stored = localStorage.getItem("chat_lastViewed");
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error loading lastViewed from storage:", error);
    return {};
  }
};

// Save lastViewed to localStorage
const saveLastViewedToStorage = (lastViewed) => {
  try {
    localStorage.setItem("chat_lastViewed", JSON.stringify(lastViewed));
  } catch (error) {
    console.error("Error saving lastViewed to storage:", error);
  }
};

const viewedTimestampMs = (value) => {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/** Keep the newest lastViewed per channel so a stale Realm/server snapshot cannot un-read a chat. */
const mergeLastViewedMaps = (base = {}, incoming = {}) => {
  const merged = { ...base };
  Object.entries(incoming || {}).forEach(([channelId, timestamp]) => {
    if (!channelId || !timestamp) return;
    if (viewedTimestampMs(timestamp) >= viewedTimestampMs(merged[channelId])) {
      merged[channelId] = timestamp;
    }
  });
  return merged;
};

const persistLastViewedToRealm = (channelId, timestamp) => {
  if (!channelId || !timestamp) return;
  try {
    let userId = null;
    try {
      userId = JSON.parse(localStorage.getItem("workloop_user") || "null")?.id;
    } catch {
      userId = null;
    }
    const docId = `${userId || "local"}:${channelId}`;
    upsertRealmDocs("UserChannelView", [{
      id: docId,
      _id: docId,
      userId: userId || undefined,
      channelId: String(channelId),
      lastViewed: timestamp,
      updatedAt: timestamp,
    }]);
  } catch {
    // local persist is best-effort
  }
};

/** Client optimistic ids from ChatWindow (temp-...) — not deduped against server UUIDs */
const isClientTempMessageId = (id) =>
  typeof id === "string" && (id.startsWith("temp-") || id.startsWith("pending-"));

const normalizeMessageText = (content) =>
  String(content || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const isLocalEchoRow = (msg) => {
  if (!msg) return false;
  if (msg.id && !isClientTempMessageId(msg.id)) return false;
  if (msg.isOptimistic || msg.queued || msg.status === "pending") return true;
  if (msg.syncStatus === "pending") return true;
  return isClientTempMessageId(msg.id) || isClientTempMessageId(msg.tempId);
};

/** Drop temp/pending bubbles once the matching server/WebSocket row exists. */
const collapseLocalEchoDuplicates = (messages) => {
  if (!messages || messages.length < 2) return messages;
  const consumed = new Set();
  for (let i = 0; i < messages.length; i += 1) {
    const server = messages[i];
    if (!server?.id || isClientTempMessageId(server.id) || isLocalEchoRow(server)) continue;
    const serverText = normalizeMessageText(server.content);
    const serverSender = String(server.senderId || "");
    for (let j = 0; j < messages.length; j += 1) {
      if (i === j || consumed.has(j)) continue;
      const local = messages[j];
      if (!isLocalEchoRow(local)) continue;
      const localClientId = String(local.tempId || local.clientMessageId || local.id || '');
      const serverClientId = String(server.clientMessageId || server.tempId || '');
      const idMatch = Boolean(localClientId && serverClientId && localClientId === serverClientId);
      if (!idMatch) {
        if (String(local.senderId || "") !== serverSender) continue;
        const localText = normalizeMessageText(local.content);
        if (!serverText && !localText) continue;
        if (localText !== serverText) continue;
      }
      consumed.add(j);
      if (!server.tempId) {
        server.tempId = local.tempId || local.id;
      }
    }
  }
  if (!consumed.size) return messages;
  return messages.filter((_, index) => !consumed.has(index));
};

const dedupeChannelMessages = (messages) =>
  collapseLocalEchoDuplicates(dedupeDuplicateServerIdsPreserveOrder(messages));

const isPreservableLocalMessage = (msg) => {
  if (!msg) return false;
  if (msg.isOptimistic || msg.queued || msg.status === "pending" || msg.status === "failed") {
    return true;
  }
  if (msg.syncStatus === "pending") return true;
  if (isClientTempMessageId(msg.id) || isClientTempMessageId(msg.tempId)) return true;
  return false;
};

/**
 * When replacing a timeline window (around-target), keep in-flight / just-sent rows
 * that the server window may not include yet — otherwise the bubble vanishes until WS/refetch.
 */
const preserveLocalMessagesAcrossReplace = (existingMessages, incomingMessages) => {
  const existing = existingMessages || [];
  const incoming = incomingMessages || [];
  if (!existing.length) return incoming;

  const incomingIds = new Set(
    incoming.map((m) => m?.id).filter(Boolean).map(String)
  );
  const incomingTempIds = new Set(
    incoming.map((m) => m?.tempId).filter(Boolean).map(String)
  );

  const now = Date.now();
  const RECENT_SENT_MS = 20000;

  const preserved = [];
  for (const m of existing) {
    const id = m?.id != null ? String(m.id) : null;
    const tempId = m?.tempId != null ? String(m.tempId) : null;
    if (id && incomingIds.has(id)) continue;
    if (tempId && (incomingTempIds.has(tempId) || incomingIds.has(tempId))) continue;

    if (isPreservableLocalMessage(m)) {
      preserved.push(m);
      continue;
    }

    // Server ACK that may be missing from a stale around-window (tempId alone is sticky — do not use it)
    const ts = new Date(m.createdAt || m.timestamp || 0).getTime();
    if (
      m.status === "sent" &&
      Number.isFinite(ts) &&
      now - ts >= 0 &&
      now - ts < RECENT_SENT_MS
    ) {
      preserved.push(m);
    }
  }

  if (!preserved.length) return incoming;
  return dedupeDuplicateServerIdsPreserveOrder(
    [...incoming, ...preserved].sort((a, b) => {
      const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
      const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
      return timeA - timeB;
    })
  );
};

const applySyncedOutboundMessages = (state, items) => {
  if (!items?.length) return;
  for (const item of items) {
    const channelId = item.channelId;
    const serverMessage = item.serverMessage;
    if (!channelId || !serverMessage || !state.messages[channelId]) continue;
    const list = state.messages[channelId];
    const index = list.findIndex(
      (m) =>
        (item.tempId && m.tempId === item.tempId) ||
        (item.localId && m.id === item.localId) ||
        (m.isOptimistic && item.tempId && m.tempId === item.tempId)
    );
    if (index === -1) continue;
    const sticky = mergeStickyDeliveryFields(list[index], serverMessage);
    list[index] = {
      ...serverMessage,
      ...sticky,
      createdAt: list[index].createdAt || serverMessage.createdAt,
      timestamp: list[index].timestamp || list[index].createdAt || serverMessage.createdAt,
      tempId: list[index].tempId || item.tempId,
      status: "sent",
      isOptimistic: false,
      queued: false,
      syncStatus: "synced",
    };
    state.messages[channelId] = dedupeChannelMessages(list);
  }
};

/**
 * Merge duplicate rows that share the same server message id (e.g. WebSocket + REST race).
 * Preserves chronological order; prefers the row with tempId for stable React keys.
 */
const dedupeDuplicateServerIdsPreserveOrder = (messages) => {
  if (!messages || messages.length === 0) return messages;
  const out = [];
  const indexByServerId = new Map();
  for (const m of messages) {
    if (!m.id || isClientTempMessageId(m.id)) {
      out.push(m);
      continue;
    }
    const sid = m.id;
    if (!indexByServerId.has(sid)) {
      indexByServerId.set(sid, out.length);
      out.push(m);
    } else {
      const idx = indexByServerId.get(sid);
      const existing = out[idx];
      const sticky = mergeStickyDeliveryFields(existing, m);
      const merged =
        existing.tempId && !m.tempId
          ? {
              ...m,
              ...existing,
              ...sticky,
              tempId: existing.tempId,
              createdAt: existing.createdAt || m.createdAt,
              timestamp:
                existing.timestamp ||
                existing.createdAt ||
                m.timestamp ||
                m.createdAt,
            }
          : !existing.tempId && m.tempId
            ? {
                ...existing,
                ...m,
                ...sticky,
                tempId: m.tempId,
                createdAt: existing.createdAt || m.createdAt,
                timestamp:
                  existing.timestamp ||
                  existing.createdAt ||
                  m.timestamp ||
                  m.createdAt,
              }
            : { ...existing, ...m, ...sticky };
      out[idx] = merged;
    }
  }
  return out;
};

export const emptyChatState = () => ({
  currentChannel: null,
  messages: {},
  typingUsers: {},
  onlineUsers: [],
  loading: false,
  error: null,
  lastViewed: {},
  unreadCounts: {},
  unreadCountsStatus: 'idle',
  mentionAlerts: [],
  dateRangeSearch: null,
  // Bumped when sticking to latest / switching channel so stale around-target fetches are ignored
  aroundFetchEpoch: 0,
  lastActivityByChannelId: {},
});

const initialState = {
  ...emptyChatState(),
  lastViewed: loadLastViewedFromStorage(),
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setCurrentChannel: (state, action) => {
      const previousChannel = state.currentChannel;
      state.currentChannel = action.payload;
      state.aroundFetchEpoch = (state.aroundFetchEpoch || 0) + 1;

      // Track view change
      if (action.payload?.id) {
        activityService.trackViewChanged(
          action.payload.type === 'DIRECT_MESSAGE' ? 'Direct Message' : 'Channel',
          action.payload.id
        );
      }

      // Mark previous channel as read when switching
      if (previousChannel?.id) {
        const timestamp = new Date().toISOString();
        state.lastViewed[previousChannel.id] = timestamp;
        state.unreadCounts[previousChannel.id] = 0;
        state.mentionAlerts = state.mentionAlerts.filter(
          (alert) => alert.channelId !== previousChannel.id
        );
        saveLastViewedToStorage(state.lastViewed);
        persistLastViewedToRealm(previousChannel.id, timestamp);
        // Save to backend (fire and forget)
        const token = localStorage.getItem("token");
        if (token) {
          axios
            .put(
              `${API_URL}/messages/channel/${previousChannel.id}/viewed`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            )
            .catch((err) =>
              console.error("Failed to save lastViewed to backend:", err)
            );
        }
      }

      // Mark new channel as read
      if (action.payload?.id) {
        const timestamp = new Date().toISOString();
        state.lastViewed[action.payload.id] = timestamp;
        state.unreadCounts[action.payload.id] = 0;
        state.mentionAlerts = state.mentionAlerts.filter(
          (alert) => alert.channelId !== action.payload.id
        );
        saveLastViewedToStorage(state.lastViewed);
        persistLastViewedToRealm(action.payload.id, timestamp);
        const token = localStorage.getItem("token");
        if (token) {
          axios
            .put(
              `${API_URL}/messages/channel/${action.payload.id}/viewed`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            )
            .catch((err) =>
              console.error("Failed to save lastViewed to backend:", err)
            );
        }
      }
    },
    invalidateAroundMessageFetches: (state) => {
      state.aroundFetchEpoch = (state.aroundFetchEpoch || 0) + 1;
    },
    markChannelAsRead: (state, action) => {
      const channelId = action.payload;
      const timestamp = new Date().toISOString();
      state.lastViewed[channelId] = timestamp;
      state.unreadCounts[channelId] = 0;
      state.mentionAlerts = state.mentionAlerts.filter(
        (alert) => alert.channelId !== channelId
      );
      saveLastViewedToStorage(state.lastViewed);
      persistLastViewedToRealm(channelId, timestamp);
      // Save to backend (fire and forget)
      const token = localStorage.getItem("token");
      if (token) {
        axios
          .put(
            `${API_URL}/messages/channel/${channelId}/viewed`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .catch((err) =>
            console.error("Failed to save lastViewed to backend:", err)
          );
      }
    },
    setLastViewed: (state, action) => {
      state.lastViewed = mergeLastViewedMaps(state.lastViewed, action.payload);
      saveLastViewedToStorage(state.lastViewed);
    },
    receiveMentionAlert: (state, action) => {
      const { channelId, channelName, isDirectMessage, message } = action.payload || {};
      if (!channelId || !message?.id) return;

      const alertKey = `${channelId}:${message.id}`;
      if (state.mentionAlerts.some((alert) => alert.alertKey === alertKey)) {
        return;
      }

      state.mentionAlerts.unshift({
        alertKey,
        channelId,
        channelName: channelName || 'Channel',
        isDirectMessage: !!isDirectMessage,
        message,
        receivedAt: new Date().toISOString(),
      });

      if (state.mentionAlerts.length > 50) {
        state.mentionAlerts.length = 50;
      }
    },
    dismissMentionAlert: (state, action) => {
      const alertKey = action.payload;
      state.mentionAlerts = state.mentionAlerts.filter(
        (alert) => alert.alertKey !== alertKey
      );
    },
    clearMentionAlertsForChannel: (state, action) => {
      const channelId = action.payload;
      state.mentionAlerts = state.mentionAlerts.filter(
        (alert) => alert.channelId !== channelId
      );
    },
    addOptimisticMessage: (state, action) => {
      const { channelId, message } = action.payload;
      if (!state.messages[channelId]) {
        state.messages[channelId] = [];
      }
      // Add optimistic message with pending status
      const optimisticMessage = {
        ...message,
        status: 'pending', // pending, sent, failed
        isOptimistic: true,
      };
      state.messages[channelId].push(optimisticMessage);
      bumpLastActivity(state, channelId, optimisticMessage);
      // Sort messages by timestamp
      state.messages[channelId].sort((a, b) => {
        const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
        const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
        return timeA - timeB;
      });
    },
    updateMessageStatus: (state, action) => {
      const { channelId, tempId, status, message: serverMessage } = action.payload;
      if (!state.messages[channelId]) return;
      
      const index = state.messages[channelId].findIndex(
        (m) => m.tempId === tempId || m.id === tempId
      );
      
      if (index !== -1) {
        if (status === 'sent' && serverMessage) {
          // Replace optimistic message with server message
          const sticky = mergeStickyDeliveryFields(
            state.messages[channelId][index],
            serverMessage
          );
          state.messages[channelId][index] = {
            ...serverMessage,
            ...sticky,
            status: 'sent',
            isOptimistic: false,
          };
        } else if (status === 'failed') {
          // Update status to failed
          state.messages[channelId][index] = {
            ...state.messages[channelId][index],
            status: 'failed',
          };
        }
      }
    },
    removeOptimisticMessage: (state, action) => {
      const { channelId, tempId } = action.payload;
      if (!state.messages[channelId]) return;
      state.messages[channelId] = state.messages[channelId].filter(
        (m) => !(m.tempId === tempId || m.id === tempId)
      );
    },
    addMessage: (state, action) => {
      const { channelId, message, currentUserId, skipUnreadCountIncrement } = action.payload;
      if (message?.hideFromChat) {
        return;
      }
      if (isHiddenFromUser(message, currentUserId)) {
        return;
      }
      if (!state.messages[channelId]) {
        state.messages[channelId] = [];
      }

      // Check if message already exists (avoid duplicates) by ID
      let existingIndex = state.messages[channelId].findIndex(
        (m) => m.id === message.id
      );

      const incomingClientId = String(message.clientMessageId || message.tempId || '');
      if (existingIndex === -1 && incomingClientId) {
        existingIndex = state.messages[channelId].findIndex((m) =>
          String(m.tempId || '') === incomingClientId
          || String(m.clientMessageId || '') === incomingClientId
          || String(m.id || '') === incomingClientId
        );
      }
      
      // If not found by ID, try matching a local echo by content (legacy sends without clientMessageId)
      if (existingIndex === -1 && message.id && !isClientTempMessageId(message.id) && !message.isOptimistic) {
        const incomingText = normalizeMessageText(message.content);
        const incomingSender = String(message.senderId || "");
        existingIndex = state.messages[channelId].findIndex((m) => {
          if (!isLocalEchoRow(m)) return false;
          if (incomingSender && String(m.senderId || "") !== incomingSender) return false;
          return normalizeMessageText(m.content) === incomingText;
        });
      }

      if (
        existingIndex !== -1
        && message.id
        && !isClientTempMessageId(message.id)
        && !message.isOptimistic
        && isLocalEchoRow(state.messages[channelId][existingIndex])
      ) {
        const optimisticMessage = state.messages[channelId][existingIndex];
        const sticky = mergeStickyDeliveryFields(optimisticMessage, message);
        const updatedMessage = {
          ...optimisticMessage,
          ...message,
          ...sticky,
          createdAt: optimisticMessage.createdAt || message.createdAt,
          timestamp: optimisticMessage.timestamp || optimisticMessage.createdAt || message.createdAt,
          tempId: optimisticMessage.tempId || message.tempId || message.clientMessageId,
          clientMessageId: message.clientMessageId || optimisticMessage.clientMessageId || optimisticMessage.tempId,
          isOptimistic: false,
          status: 'sent',
          attachments: preferAttachmentsWithUrls(optimisticMessage.attachments, message.attachments),
        };
        state.messages[channelId][existingIndex] = updatedMessage;
        state.messages[channelId] = dedupeChannelMessages(
          state.messages[channelId]
        );
        return;
      }

      if (existingIndex !== -1) {
        // Update existing message - merge to preserve all fields
        const existingMessage = state.messages[channelId][existingIndex];
        const sticky = mergeStickyDeliveryFields(existingMessage, message);
        // Create a new object to ensure React detects the change
        const updatedMessage = {
          ...existingMessage,
          ...message, // Override with new values (including content, isEdited, readBy, isRead, etc.)
          ...sticky,          // Keep denormalized sender fields when an update omits them (Realm sync / delta)
          attachments: preferAttachmentsWithUrls(existingMessage.attachments, message.attachments),
          senderName: (message.senderName && String(message.senderName).trim())
            ? message.senderName
            : existingMessage.senderName,
          senderProfilePicture: message.senderProfilePicture !== undefined && message.senderProfilePicture !== null
            ? message.senderProfilePicture
            : existingMessage.senderProfilePicture,
          // Preserve appointment invite RSVPs if an update omits them
          appointmentInfo: message.appointmentInfo != null
            ? message.appointmentInfo
            : existingMessage.appointmentInfo,
          eventInfo: message.eventInfo != null
            ? message.eventInfo
            : existingMessage.eventInfo,
          workItemShare: message.workItemShare != null
            ? message.workItemShare
            : existingMessage.workItemShare,
          // Preserve assignee/track when an update omits those keys
          workflowAssignedToUserId: message.workflowAssignedToUserId !== undefined
            ? message.workflowAssignedToUserId
            : existingMessage.workflowAssignedToUserId,
          workflowAssignedToName: message.workflowAssignedToName !== undefined
            ? message.workflowAssignedToName
            : existingMessage.workflowAssignedToName,
          workflowDescription: message.workflowDescription !== undefined
            ? message.workflowDescription
            : existingMessage.workflowDescription,
          workflowTitle: message.workflowTitle !== undefined
            ? message.workflowTitle
            : existingMessage.workflowTitle,
          workflowSpaceId: message.workflowSpaceId !== undefined
            ? message.workflowSpaceId
            : existingMessage.workflowSpaceId,
          workflowSpaceName: message.workflowSpaceName !== undefined
            ? message.workflowSpaceName
            : existingMessage.workflowSpaceName,
          workflowTrackId: message.workflowTrackId !== undefined
            ? message.workflowTrackId
            : existingMessage.workflowTrackId,
          workflowTrackLink: message.workflowTrackLink !== undefined
            ? message.workflowTrackLink
            : existingMessage.workflowTrackLink,
          customTags: message.customTags !== undefined
            ? message.customTags
            : existingMessage.customTags,
          translations: message.translations
            ? { ...(existingMessage.translations || {}), ...message.translations }
            : existingMessage.translations,
          translationPending: message.translationPending !== undefined
            ? message.translationPending
            : existingMessage.translationPending,
          sourceLanguage: message.sourceLanguage || existingMessage.sourceLanguage,
          displayContent: message.displayContent !== undefined
            ? message.displayContent
            : existingMessage.displayContent,
          // If replacing an optimistic message with a server message, mark as non-optimistic
          isOptimistic: existingMessage.isOptimistic && !message.isOptimistic ? false : (message.isOptimistic ?? existingMessage.isOptimistic),
          // For server messages replacing optimistic ones, mark as sent
          status: existingMessage.isOptimistic && !message.isOptimistic ? 'sent' : (message.status ?? existingMessage.status),
        };

        // Check if there's an actual change to avoid unnecessary updates
        // Check for changes in content, isEdited, updatedAt, reactions, attachments, readBy, isRead, and replyToMessageId
        const hasChanged =
          existingMessage.content !== updatedMessage.content ||
          existingMessage.isEdited !== updatedMessage.isEdited ||
          existingMessage.updatedAt !== updatedMessage.updatedAt ||
          existingMessage.isRead !== updatedMessage.isRead ||
          existingMessage.isDelivered !== updatedMessage.isDelivered ||
          existingMessage.replyToMessageId !==
          updatedMessage.replyToMessageId ||
          existingMessage.workflowAssignedToUserId !== updatedMessage.workflowAssignedToUserId ||
          existingMessage.workflowAssignedToName !== updatedMessage.workflowAssignedToName ||
          existingMessage.workflowDescription !== updatedMessage.workflowDescription ||
          existingMessage.workflowTitle !== updatedMessage.workflowTitle ||
          existingMessage.workflowSpaceId !== updatedMessage.workflowSpaceId ||
          existingMessage.workflowSpaceName !== updatedMessage.workflowSpaceName ||
          existingMessage.workflowTrackId !== updatedMessage.workflowTrackId ||
          existingMessage.workflowTrackLink !== updatedMessage.workflowTrackLink ||
          JSON.stringify(existingMessage.reactions || []) !==
          JSON.stringify(updatedMessage.reactions || []) ||
          JSON.stringify(existingMessage.attachments || []) !==
          JSON.stringify(updatedMessage.attachments || []) ||
          JSON.stringify(existingMessage.readBy || []) !==
          JSON.stringify(updatedMessage.readBy || []) ||
          JSON.stringify(existingMessage.deliveredBy || []) !==
          JSON.stringify(updatedMessage.deliveredBy || []) ||
          JSON.stringify(existingMessage.customTags || []) !==
          JSON.stringify(updatedMessage.customTags || []) ||
          JSON.stringify(existingMessage.meetingInfo || null) !==
          JSON.stringify(updatedMessage.meetingInfo || null) ||
          JSON.stringify(existingMessage.appointmentInfo || null) !==
          JSON.stringify(updatedMessage.appointmentInfo || null) ||
          JSON.stringify(existingMessage.eventInfo || null) !==
          JSON.stringify(updatedMessage.eventInfo || null) ||
          JSON.stringify(existingMessage.translations || null) !==
          JSON.stringify(updatedMessage.translations || null) ||
          existingMessage.translationPending !== updatedMessage.translationPending ||
          existingMessage.displayContent !== updatedMessage.displayContent ||
          existingMessage.sourceLanguage !== updatedMessage.sourceLanguage ||
          existingMessage.linkedMeetingMessageId !== updatedMessage.linkedMeetingMessageId;

        // Always update the message if there's a change (this ensures React re-renders)
        if (hasChanged) {
          state.messages[channelId][existingIndex] = updatedMessage;
          console.log("🔄 Updated existing message:", {
            id: message.id,
            contentChanged: existingMessage.content !== updatedMessage.content,
            isEditedChanged:
              existingMessage.isEdited !== updatedMessage.isEdited,
            oldIsRead: existingMessage.isRead,
            newIsRead: updatedMessage.isRead,
            oldReadBy: existingMessage.readBy?.length || 0,
            newReadBy: updatedMessage.readBy?.length || 0,
          });
        } else {
          // Even if no change detected, update to ensure React sees the new object reference
          // This is important for cases where the message object reference changes but values are the same
          state.messages[channelId][existingIndex] = updatedMessage;
        }
      } else {
        // Add new message at the end (bottom)
        state.messages[channelId].push(message);
      }
      bumpLastActivity(state, channelId, message);

      // Only sort if we added a new message (not if we updated/replaced an existing one)
      // This prevents UI shifts when replacing optimistic messages
      if (existingIndex === -1) {
        // Sort messages by timestamp (oldest first, newest last)
        state.messages[channelId].sort((a, b) => {
          const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
          const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
          return timeA - timeB;
        });
      }

      // Update unread count if message is new and channel is not currently viewed
      // Skip unread count increment if flag is set (e.g., when adding via polling - rely on fetchUnreadCounts instead)
      if (existingIndex === -1 && !skipUnreadCountIncrement) {
        const messageTime = new Date(
          message.createdAt || message.timestamp || Date.now()
        ).getTime();
        const lastViewedTime = state.lastViewed[channelId]
          ? new Date(state.lastViewed[channelId]).getTime()
          : 0;

        // Only increment unread count if:
        // 1. Channel is not currently being viewed, AND
        // 2. Message is newer than last viewed time (or never viewed), AND
        // 3. Message is NOT from the current user
        const isCurrentChannel = String(state.currentChannel?.id || '') === String(channelId || '');
        const isFromCurrentUser =
          currentUserId && message.senderId === currentUserId;

        // If channel was never viewed (lastViewedTime === 0), always count messages from others
        // If channel was viewed, only count messages newer than lastViewedTime
        const shouldIncrement =
          !isCurrentChannel &&
          !isFromCurrentUser &&
          (lastViewedTime === 0 || messageTime > lastViewedTime);

        console.log("🔍 Unread count evaluation:", {
          channelId,
          messageId: message.id,
          isNewMessage: existingIndex === -1,
          isCurrentChannel,
          isFromCurrentUser,
          currentUserId,
          messageSenderId: message.senderId,
          messageTime: new Date(messageTime).toISOString(),
          lastViewedTime:
            lastViewedTime > 0
              ? new Date(lastViewedTime).toISOString()
              : "never",
          shouldIncrement,
          currentUnreadCount: state.unreadCounts[channelId] || 0,
        });

        if (shouldIncrement) {
          const currentCount = state.unreadCounts[channelId] || 0;
          state.unreadCounts[channelId] = currentCount + 1;
          console.log("✅ Unread count INCREMENTED:", {
            channelId,
            previousCount: currentCount,
            newCount: state.unreadCounts[channelId],
            isCurrentChannel,
            isFromCurrentUser,
            messageTime: new Date(messageTime).toISOString(),
            lastViewedTime:
              lastViewedTime > 0
                ? new Date(lastViewedTime).toISOString()
                : "never",
            messageId: message.id,
            senderId: message.senderId,
            currentUserId,
          });
        } else {
          console.log("⏭️ Unread count NOT incremented:", {
            channelId,
            reason: isCurrentChannel
              ? "channel is currently viewed"
              : isFromCurrentUser
                ? "message is from current user"
                : lastViewedTime > 0 && messageTime <= lastViewedTime
                  ? "message is older than last viewed"
                  : "unknown",
            isCurrentChannel,
            isFromCurrentUser,
            messageTime: new Date(messageTime).toISOString(),
            lastViewedTime:
              lastViewedTime > 0
                ? new Date(lastViewedTime).toISOString()
                : "never",
            messageId: message.id,
            senderId: message.senderId,
            currentUserId,
          });
        }
      } else {
        console.log(
          "🔄 Message already exists, skipping unread count update:",
          {
            channelId,
            messageId: message.id,
            existingIndex,
          }
        );
      }

      state.messages[channelId] = dedupeChannelMessages(
        state.messages[channelId]
      );
    },
    updateMessage: (state, action) => {
      const { channelId, message } = action.payload;
      if (state.messages[channelId]) {
        const index = state.messages[channelId].findIndex(
          (m) => m.id === message.id
        );
        if (index !== -1) {
          state.messages[channelId][index] = message;
        }
      }
    },
    removeMessage: (state, action) => {
      const { channelId, messageId } = action.payload;
      if (state.messages[channelId]) {
        state.messages[channelId] = state.messages[channelId].filter(
          (m) => m.id !== messageId
        );
      }
    },
    setTypingUser: (state, action) => {
      const { channelId, username, isTyping } = action.payload;
      if (!state.typingUsers[channelId]) {
        state.typingUsers[channelId] = [];
      }

      if (isTyping) {
        if (!state.typingUsers[channelId].includes(username)) {
          state.typingUsers[channelId].push(username);
        }
      } else {
        state.typingUsers[channelId] = state.typingUsers[channelId].filter(
          (user) => user !== username
        );
      }
    },
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
    },
    clearMessages: (state) => {
      state.messages = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    setUnreadCounts: (state, action) => {
      // Merge the provided unread counts with existing ones
      state.unreadCounts = { ...state.unreadCounts, ...action.payload };
    },
    clearDateRangeSearch: (state) => {
      state.dateRangeSearch = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => emptyChatState())
      // Fetch messages
      .addCase(fetchMessages.pending, (state, action) => {
        const channelId = action.meta?.arg?.channelId;
        const cached = channelId ? state.messages[channelId] : null;
        if (!cached?.length) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        const { channelId } = action.payload || {};
        const incoming = asMessageArray(action.payload?.messages);
        if (!channelId) {
          state.error = null;
          return;
        }

        // Merge fetched messages with existing messages (from WebSocket) to avoid losing real-time updates
        const existingMessages = state.messages[channelId] || [];
        const messageMap = new Map();

        existingMessages.forEach((msg) => {
          const id = messageDocId(msg);
          if (id) messageMap.set(id, msg);
        });

        incoming.forEach((msg) => {
          const id = messageDocId(msg);
          if (!id) return;
          const existing = messageMap.get(id);
          const fullMessage = {
            ...existing,
            ...msg,
            id,
            content: msg.content || existing?.content || "",
            senderId: msg.senderId,
            senderName: (msg.senderName && String(msg.senderName).trim())
              ? msg.senderName
              : (existing?.senderName || msg.senderName),
            senderProfilePicture: msg.senderProfilePicture
              || existing?.senderProfilePicture
              || null,
            channelId: msg.channelId || existing?.channelId || channelId,
            type: msg.type,
            attachmentGroupName: msg.attachmentGroupName || existing?.attachmentGroupName || null,
            resourceGroupId: msg.resourceGroupId || existing?.resourceGroupId || null,
            reactions: msg.reactions || existing?.reactions || [],
            replyToMessageId: Object.prototype.hasOwnProperty.call(msg, "replyToMessageId")
              ? (msg.replyToMessageId || null)
              : (existing?.replyToMessageId || null),
            mentionedUserIds: msg.mentionedUserIds || existing?.mentionedUserIds || [],
            isEdited: msg.isEdited || existing?.isEdited || false,
            isPinned: msg.isPinned || existing?.isPinned || false,
            ...mergeStickyDeliveryFields(existing, msg),
            attachments: preferAttachmentsWithUrls(existing?.attachments, msg.attachments),
            linkPreview: msg.linkPreview || existing?.linkPreview || null,
            meetingInfo: msg.meetingInfo || existing?.meetingInfo || null,
            appointmentInfo: msg.appointmentInfo || existing?.appointmentInfo || null,
            eventInfo: msg.eventInfo || existing?.eventInfo || null,
            workItemShare: msg.workItemShare || existing?.workItemShare || null,
            attendanceInfo: msg.attendanceInfo || existing?.attendanceInfo || null,
            notificationInfo: msg.notificationInfo || existing?.notificationInfo || null,
            linkedMeetingMessageId: msg.linkedMeetingMessageId || existing?.linkedMeetingMessageId || null,
            workflowTrackId: msg.workflowTrackId || existing?.workflowTrackId || null,
            workflowTrackLink: msg.workflowTrackLink || existing?.workflowTrackLink || null,
            customTags: msg.customTags || existing?.customTags || [],
            createdAt: msg.createdAt || existing?.createdAt,
            updatedAt: msg.updatedAt || existing?.updatedAt,
          };
          messageMap.set(id, fullMessage);
        });

        // Convert back to array and sort by timestamp (oldest first, newest last)
        const mergedMessages = Array.from(messageMap.values()).sort((a, b) => {
          const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
          const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
          return timeA - timeB;
        });

        state.messages[channelId] = dedupeChannelMessages(mergedMessages);
        const latestMerged = state.messages[channelId][state.messages[channelId].length - 1];
        bumpLastActivity(state, channelId, latestMerged);

        // Mark channel as read when messages are fetched (user is viewing it)
        if (state.currentChannel?.id === channelId) {
          const timestamp = new Date().toISOString();
          state.lastViewed[channelId] = timestamp;
          state.unreadCounts[channelId] = 0;
          saveLastViewedToStorage(state.lastViewed);
          persistLastViewedToRealm(channelId, timestamp);
        }

        state.error = null;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Load older messages
      .addCase(loadOlderMessages.pending, (state) => {
        state.error = null;
      })
      .addCase(loadOlderMessages.fulfilled, (state, action) => {
        const { channelId, messages } = action.payload;

        if (!state.messages[channelId]) {
          state.messages[channelId] = [];
        }

        // Prepend older messages to the beginning of the array
        const existingMessages = state.messages[channelId] || [];
        const messageMap = new Map();

        // Add existing messages to map
        existingMessages.forEach((msg) => {
          messageMap.set(msg.id, msg);
        });

        // Add older messages (they should be older than existing ones)
        messages.forEach((msg) => {
          const existing = messageMap.get(msg.id);
          const sticky = mergeStickyDeliveryFields(existing, msg);
          const fullMessage = {
            ...msg,
            id: msg.id,
            content: msg.content || "",
            senderId: msg.senderId,
            senderName: msg.senderName,
            senderProfilePicture: msg.senderProfilePicture || null,
            channelId: msg.channelId,
            type: msg.type,
            attachments: msg.attachments || [],
            attachmentGroupName: msg.attachmentGroupName || null,
            resourceGroupId: msg.resourceGroupId || null,
            reactions: msg.reactions || [],
            replyToMessageId: msg.replyToMessageId || null,
            mentionedUserIds: msg.mentionedUserIds || [],
            isEdited: msg.isEdited || false,
            isPinned: msg.isPinned || false,
            ...sticky,
            linkPreview: msg.linkPreview || null,
            meetingInfo: msg.meetingInfo || null,
            appointmentInfo: msg.appointmentInfo || null,
            eventInfo: msg.eventInfo || null,
            workItemShare: msg.workItemShare || null,
            attendanceInfo: msg.attendanceInfo || null,
            notificationInfo: msg.notificationInfo || null,
            linkedMeetingMessageId: msg.linkedMeetingMessageId || null,
            workflowTrackId: msg.workflowTrackId || null,
            workflowTrackLink: msg.workflowTrackLink || null,
            customTags: msg.customTags || [],
            createdAt: msg.createdAt,
            updatedAt: msg.updatedAt,
          };
          messageMap.set(msg.id, fullMessage);
        });

        // Convert back to array and sort by timestamp (oldest first, newest last)
        const mergedMessages = Array.from(messageMap.values()).sort((a, b) => {
          const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
          const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
          return timeA - timeB;
        });

        state.messages[channelId] = dedupeChannelMessages(mergedMessages);
        state.error = null;
      })
      .addCase(loadOlderMessages.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchMessagesAroundTarget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessagesAroundTarget.fulfilled, (state, action) => {
        state.loading = false;
        const { channelId, messages, epochAtStart } = action.payload;

        // Stick-to-latest / channel switch invalidated this in-flight around fetch
        if (
          epochAtStart != null &&
          epochAtStart !== (state.aroundFetchEpoch || 0)
        ) {
          state.error = null;
          return;
        }

        const existingById = new Map(
          (state.messages[channelId] || [])
            .filter((m) => m?.id != null)
            .map((m) => [String(m.id), m])
        );

        const normalizedMessages = messages.map((msg) => {
          const existing = existingById.get(String(msg.id));
          const sticky = mergeStickyDeliveryFields(existing, msg);
          return {
            ...msg,
            id: msg.id,
            content: msg.content || "",
            senderId: msg.senderId,
            senderName: msg.senderName,
            senderProfilePicture: msg.senderProfilePicture || null,
            channelId: msg.channelId,
            type: msg.type,
            attachments: msg.attachments || [],
            reactions: msg.reactions || [],
            replyToMessageId: msg.replyToMessageId || null,
            mentionedUserIds: msg.mentionedUserIds || [],
            isEdited: msg.isEdited || false,
            isPinned: msg.isPinned || false,
            ...sticky,
            linkPreview: msg.linkPreview || null,
            createdAt: msg.createdAt,
            updatedAt: msg.updatedAt,
          };
        });

        const sortedIncoming = normalizedMessages.sort((a, b) => {
          const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
          const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
          return timeA - timeB;
        });

        state.messages[channelId] = preserveLocalMessagesAcrossReplace(
          state.messages[channelId] || [],
          sortedIncoming
        );

        if (state.currentChannel?.id === channelId) {
          const timestamp = new Date().toISOString();
          state.lastViewed[channelId] = timestamp;
          state.unreadCounts[channelId] = 0;
          saveLastViewedToStorage(state.lastViewed);
          persistLastViewedToRealm(channelId, timestamp);
        }

        state.error = null;
      })
      .addCase(fetchMessagesAroundTarget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        const message = action.payload;
        const tempId = action.meta.arg?.tempId;
        const channelId = message.channelId;

        if (!state.messages[channelId]) {
          state.messages[channelId] = [];
        }

        let replacedOptimistic = false;
        if (tempId) {
          const optimisticIndex = state.messages[channelId].findIndex(
            (m) =>
              m.tempId === tempId ||
              m.id === tempId ||
              m.clientMessageId === tempId ||
              (m.isOptimistic && m.tempId === tempId)
          );
          if (optimisticIndex !== -1) {
            const optimisticMessage = state.messages[channelId][optimisticIndex];
            const sticky = mergeStickyDeliveryFields(optimisticMessage, message);
            state.messages[channelId][optimisticIndex] = {
              ...message,
              ...sticky,
              createdAt: optimisticMessage.createdAt || message.createdAt,
              timestamp:
                optimisticMessage.timestamp ||
                optimisticMessage.createdAt ||
                message.createdAt,
              tempId: optimisticMessage.tempId || tempId || message.clientMessageId,
              clientMessageId: message.clientMessageId || tempId,
              status: message.queued ? "pending" : "sent",
              isOptimistic: Boolean(message.queued),
              attachments:
                message.queued && optimisticMessage.attachments?.length
                  ? optimisticMessage.attachments
                  : (message.attachments || optimisticMessage.attachments),
            };
            const sid = message.id;
            const list = state.messages[channelId];
            state.messages[channelId] = list.filter(
              (m, i) => m.id !== sid || i === optimisticIndex
            );
            replacedOptimistic = true;
          }
        }

        if (!replacedOptimistic) {
          const existingIndex = state.messages[channelId].findIndex(
            (m) => m.id === message.id
          );
          if (existingIndex === -1) {
            state.messages[channelId].push({
              ...message,
              status: "sent",
              isOptimistic: false,
            });
            state.messages[channelId].sort((a, b) => {
              const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
              const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
              return timeA - timeB;
            });
          }
        }

        state.messages[channelId] = dedupeChannelMessages(
          state.messages[channelId]
        );
        state.error = null;
      })
      .addCase(flushOutboundMessages.fulfilled, (state, action) => {
        applySyncedOutboundMessages(state, action.payload);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        const tempId = action.meta.arg.tempId;
        const channelId = action.meta.arg.channelId;
        
        // Update optimistic message status to failed
        if (tempId && channelId && state.messages[channelId]) {
          const optimisticIndex = state.messages[channelId].findIndex(
            (m) => m.tempId === tempId || m.id === tempId
          );
          if (optimisticIndex !== -1) {
            state.messages[channelId][optimisticIndex] = {
              ...state.messages[channelId][optimisticIndex],
              status: 'failed',
            };
          }
        }
        
        state.error = action.payload;
      })
      // Add reaction
      .addCase(addReaction.pending, (state, action) => {
        const {
          messageId,
          emoji,
          channelId,
          userId,
          workflowTitle,
          workflowDescription,
          workflowSpaceId,
          workflowSpaceName,
          urgentDeadlineAt,
          completionTimeInMinutes,
        } = action.meta.arg;
        if (!userId || !messageId || !emoji) return;
        const location = findMessageLocation(state, messageId, channelId);
        if (!location) return;
        const existingMessage = state.messages[location.channelId][location.index];
        const title = typeof workflowTitle === 'string' ? workflowTitle.trim() : '';
        const wasUrgent = (existingMessage.reactions || []).some(
          (reaction) => reaction?.emoji === 'wf:urgency:urgent',
        );
        const clearingUrgent = (emoji === 'wf:urgency:urgent' || String(emoji).startsWith('wf:urgency:'))
          && wasUrgent;
        const nextReactions = applyOptimisticReaction(existingMessage.reactions, emoji, userId);
        const minutes = Number(completionTimeInMinutes);
        state.messages[location.channelId][location.index] = {
          ...existingMessage,
          reactions: nextReactions,
          workflowByCategory: getActiveWorkflowByCategory(nextReactions),
          ...(Number.isFinite(minutes) && minutes > 0 ? { completionTimeInMinutes: minutes } : {}),
          ...(title ? { workflowTitle: title.slice(0, 200) } : {}),
          ...(workflowDescription != null
            ? { workflowDescription: String(workflowDescription) }
            : {}),
          ...(workflowSpaceId ? { workflowSpaceId: String(workflowSpaceId) } : {}),
          ...(workflowSpaceName ? { workflowSpaceName: String(workflowSpaceName) } : {}),
          ...(clearingUrgent
            ? { urgentDeadlineAt: null }
            : (urgentDeadlineAt
              ? { urgentDeadlineAt: String(urgentDeadlineAt) }
              : {})),
        };
      })
      .addCase(addReaction.fulfilled, (state, action) => {
        const updatedMessage = action.payload;
        if (!updatedMessage?.id) return;
        const location = findMessageLocation(state, updatedMessage.id, updatedMessage.channelId);
        if (!location) return;
        const existingMessage = state.messages[location.channelId][location.index];
        const nextReactions = updatedMessage.reactions || existingMessage.reactions;
        state.messages[location.channelId][location.index] = {
          ...existingMessage,
          ...updatedMessage,
          senderId: existingMessage.senderId,
          senderName: existingMessage.senderName,
          senderProfilePicture: existingMessage.senderProfilePicture,
          reactions: nextReactions,
          workflowByCategory: getActiveWorkflowByCategory(nextReactions),
          updatedAt: updatedMessage.updatedAt || existingMessage.updatedAt,
        };
      })
      .addCase(addReaction.rejected, (state, action) => {
        const restore = action.payload?.restore;
        if (!restore?.channelId || !restore?.messageId) return;
        const channelMessages = state.messages[restore.channelId];
        if (!channelMessages) return;
        const index = channelMessages.findIndex(
          (m) => m.id === restore.messageId || m.tempId === restore.messageId,
        );
        if (index === -1) return;
        state.messages[restore.channelId][index] = {
          ...channelMessages[index],
          reactions: restore.reactions,
          workflowByCategory: restore.workflowByCategory,
          completionTimeInMinutes: restore.completionTimeInMinutes,
          urgentDeadlineAt: restore.urgentDeadlineAt,
          workflowTitle: restore.workflowTitle,
          workflowDescription: restore.workflowDescription,
          workflowSpaceId: restore.workflowSpaceId,
          workflowSpaceName: restore.workflowSpaceName,
        };
      })
      .addCase(updateWorkflowTrack.fulfilled, (state, action) => {
        const updatedMessage = action.payload;
        if (state.messages[updatedMessage.channelId]) {
          const index = state.messages[updatedMessage.channelId].findIndex(
            (m) => m.id === updatedMessage.id || m.tempId === updatedMessage.id,
          );
          if (index !== -1) {
            const existingMessage =
              state.messages[updatedMessage.channelId][index];
            state.messages[updatedMessage.channelId][index] = {
              ...existingMessage,
              ...updatedMessage,
              senderId: existingMessage.senderId,
              senderName: existingMessage.senderName,
              senderProfilePicture: existingMessage.senderProfilePicture,
              workflowTrackId: updatedMessage.workflowTrackId || null,
              workflowTrackLink: updatedMessage.workflowTrackLink || null,
              updatedAt: updatedMessage.updatedAt || existingMessage.updatedAt,
            };
          }
        }
      })
      .addCase(addMessageCustomTag.fulfilled, (state, action) => {
        const updatedMessage = action.payload;
        if (!state.messages[updatedMessage.channelId]) return;
        const index = state.messages[updatedMessage.channelId].findIndex(
          (m) => m.id === updatedMessage.id || m.tempId === updatedMessage.id,
        );
        if (index === -1) return;
        const existingMessage = state.messages[updatedMessage.channelId][index];
        state.messages[updatedMessage.channelId][index] = {
          ...existingMessage,
          ...updatedMessage,
          senderId: existingMessage.senderId,
          senderName: existingMessage.senderName,
          senderProfilePicture: existingMessage.senderProfilePicture,
          customTags: updatedMessage.customTags || [],
          updatedAt: updatedMessage.updatedAt || existingMessage.updatedAt,
        };
      })
      .addCase(removeMessageCustomTag.fulfilled, (state, action) => {
        const updatedMessage = action.payload;
        if (!state.messages[updatedMessage.channelId]) return;
        const index = state.messages[updatedMessage.channelId].findIndex(
          (m) => m.id === updatedMessage.id || m.tempId === updatedMessage.id,
        );
        if (index === -1) return;
        const existingMessage = state.messages[updatedMessage.channelId][index];
        state.messages[updatedMessage.channelId][index] = {
          ...existingMessage,
          ...updatedMessage,
          senderId: existingMessage.senderId,
          senderName: existingMessage.senderName,
          senderProfilePicture: existingMessage.senderProfilePicture,
          customTags: updatedMessage.customTags || [],
          updatedAt: updatedMessage.updatedAt || existingMessage.updatedAt,
        };
      })
      .addCase(assignWorkflowMessage.pending, (state, action) => {
        const { messageId, userId, userName, channelId } = action.meta.arg || {};
        if (!messageId) return;
        const location = findMessageLocation(state, messageId, channelId);
        if (!location) return;
        const existingMessage = state.messages[location.channelId][location.index];
        state.messages[location.channelId][location.index] = {
          ...existingMessage,
          workflowAssignedToUserId: userId || null,
          workflowAssignedToName: userName || existingMessage.workflowAssignedToName || '',
        };
      })
      .addCase(assignWorkflowMessage.fulfilled, (state, action) => {
        const updatedMessage = action.payload;
        if (!state.messages[updatedMessage.channelId]) return;
        const index = state.messages[updatedMessage.channelId].findIndex(
          (m) => m.id === updatedMessage.id || m.tempId === updatedMessage.id,
        );
        if (index === -1) return;
        const existingMessage = state.messages[updatedMessage.channelId][index];
        state.messages[updatedMessage.channelId][index] = {
          ...existingMessage,
          ...updatedMessage,
          senderId: existingMessage.senderId,
          senderName: existingMessage.senderName,
          senderProfilePicture: existingMessage.senderProfilePicture,
          reactions: updatedMessage.reactions || existingMessage.reactions,
          workflowAssignedToUserId: updatedMessage.workflowAssignedToUserId,
          workflowAssignedToName: updatedMessage.workflowAssignedToName,
          updatedAt: updatedMessage.updatedAt || existingMessage.updatedAt,
        };
      })
      .addCase(assignWorkflowMessage.rejected, (state, action) => {
        const restore = action.payload?.restore;
        if (!restore?.channelId || !restore?.messageId) return;
        const channelMessages = state.messages[restore.channelId];
        if (!channelMessages) return;
        const index = channelMessages.findIndex(
          (m) => m.id === restore.messageId || m.tempId === restore.messageId,
        );
        if (index === -1) return;
        state.messages[restore.channelId][index] = {
          ...channelMessages[index],
          workflowAssignedToUserId: restore.workflowAssignedToUserId,
          workflowAssignedToName: restore.workflowAssignedToName,
        };
      })
      // Fetch unread counts
      .addCase(fetchUnreadCounts.pending, (state) => {
        state.unreadCountsStatus = 'pending';
      })
      .addCase(fetchUnreadCounts.fulfilled, (state, action) => {
        state.unreadCountsStatus = 'idle';
        const fetchedCounts = action.payload || {};
        const requestLastViewed = action.meta?.arg?.lastViewed || {};
        Object.keys(fetchedCounts).forEach((channelId) => {
          if (String(state.currentChannel?.id || '') === String(channelId || '')) {
            state.unreadCounts[channelId] = 0;
            return;
          }
          const localViewed = viewedTimestampMs(state.lastViewed[channelId]);
          const requestViewed = viewedTimestampMs(requestLastViewed[channelId]);
          if (localViewed > requestViewed) {
            return;
          }
          state.unreadCounts[channelId] = fetchedCounts[channelId] ?? 0;
        });
      })
      .addCase(fetchUnreadCounts.rejected, (state) => {
        state.unreadCountsStatus = 'idle';
      })
      // Fetch lastViewed from backend
      .addCase(fetchLastViewed.fulfilled, (state, action) => {
        if (action.payload) {
          state.lastViewed = mergeLastViewedMaps(state.lastViewed, action.payload);
          saveLastViewedToStorage(state.lastViewed);
        }
      })
      // Save lastViewed to backend
      .addCase(saveLastViewedToBackend.fulfilled, (state, action) => {
        // Update local state with the saved timestamp
        if (action.payload) {
          state.lastViewed[action.payload.channelId] = action.payload.timestamp;
          saveLastViewedToStorage(state.lastViewed);
        }
      })
      // Fetch messages by date range
      .addCase(fetchMessagesByDateRange.pending, (state, action) => {
        state.dateRangeSearch = {
          channelId: action.meta.arg.channelId,
          startDate: action.meta.arg.startDate,
          endDate: action.meta.arg.endDate,
          userId: action.meta.arg.userId || null,
          messages: null,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchMessagesByDateRange.fulfilled, (state, action) => {
        const { channelId, startDate, endDate, userId, messages } = action.payload;
        if (state.dateRangeSearch && state.dateRangeSearch.channelId === channelId) {
          state.dateRangeSearch.messages = messages;
          state.dateRangeSearch.loading = false;
          state.dateRangeSearch.error = null;
          state.dateRangeSearch.startDate = startDate;
          state.dateRangeSearch.endDate = endDate;
          state.dateRangeSearch.userId = userId;
        }
      })
      .addCase(fetchMessagesByDateRange.rejected, (state, action) => {
        if (state.dateRangeSearch) {
          state.dateRangeSearch.loading = false;
          state.dateRangeSearch.error = action.payload;
          state.dateRangeSearch.messages = null;
        }
      });
  },
});

export const {
  setCurrentChannel,
  addMessage,
  addOptimisticMessage,
  updateMessageStatus,
  removeOptimisticMessage,
  updateMessage,
  removeMessage,
  setTypingUser,
  setOnlineUsers,
  clearMessages,
  clearError,
  markChannelAsRead,
  invalidateAroundMessageFetches,
  setUnreadCounts,
  setLastViewed,
  receiveMentionAlert,
  dismissMentionAlert,
  clearMentionAlertsForChannel,
  clearDateRangeSearch,
} = chatSlice.actions;

export default chatSlice.reducer;
